import React, { createRef, useEffect } from 'react';
import { COLOR_05, COLOR_25, COLOR_26, COLOR_35, COLOR_36, COUNTRY_COUNT, ROUTE_PAGE_QUOTA_ANALYSIS } from '../../../constants';
import { useAppState } from '../../../hooks/use-app-state.hook';
import { CountryProfile } from '../../../types';
import { getTaxonomyName } from '../../../utils';
import { Cell, Grid } from '../../layout/grid/grid.component';
import {
    globalQuotaTypeChartClassName,
    globalQuotaTypeChartLabelNumberClassName,
    globalQuotaTypeChartLabelTextClassName,
    globalQuotaTypeChartStatisticsColumn,
    globalQuotaTypeChartStatisticsColumnTitle,
    globalQuotaTypeChartStatisticsColumnItem,
    globalQuotaTypeChartStatisticsColumnNumberClassName,
    globalQuotaTypeChartStatisticsColumnName,
    resetSpacingsClassName,
    globalQuotaTypeChartDonutContainerClassName,
    globalQuotaTypeChartDonutClassName,
    parliamentAndLocalGovernmentLinksClassName,
    quotaTypeChartStatisticsCaptionClassName,
    quotaTypeChartStatisticsCaptionNumberClassName,
    quotaTypeChartStatisticsCaptionDescriptionClassName,
    quotaTypeChartStatisticsCaptionDescriptionSmallClassName,
    globalQuotaTypeChartDoubleDonutContainerClassName,
} from './quota-type-chart.style';
import { ThemeName } from '../../../types';
import { getValuesForChart } from './quota-type-chart.utils';
import { Link } from 'react-router-dom';
import Spacer from '../../layout/spacer/spacer.component';
import { classes } from 'typestyle';
import Donut2 from '../../layout/donut-2/donut.component';
import DynamicNumber from '../../layout/dynamic-number/dynamic-number.component';

interface QuotaTypeChartProps {
    profiles?: CountryProfile[],
    profilesLocalLevel?: CountryProfile[],
    countryCount?: number,
    withCaption?: Boolean,
}

const statisticsColumn = (
    title: string,
    candidateQuotas: number,
    reservedSeatQuotas: number,
    themeName: ThemeName,
) => {
    const background = themeName === 'blue'
        ? 'linear-gradient(90deg, #F0FAFE 0%, rgba(240, 250, 254, 0) 128.88%)'
        : 'linear-gradient(90deg, #EFFDF5 0%, rgba(239, 253, 245, 0) 137.62%)';
    const colorNumber2 = themeName === 'blue' ? COLOR_25 : COLOR_35;
    const colorNumber1 = themeName === 'blue' ? COLOR_26 : COLOR_36;

    return (
        <div className={ globalQuotaTypeChartStatisticsColumn } style={ {background} }>
            <div className={ globalQuotaTypeChartStatisticsColumnTitle }>
                { title }
            </div>
            <div className={ globalQuotaTypeChartStatisticsColumnItem }>
                <div className={ globalQuotaTypeChartStatisticsColumnNumberClassName } style={ {color: colorNumber1} }>
                    { candidateQuotas }
                </div>
                <div className={ globalQuotaTypeChartStatisticsColumnName }>
                    Countries with candidate quotas
                </div>
            </div>
            <div className={ globalQuotaTypeChartStatisticsColumnItem }>
                <div className={ globalQuotaTypeChartStatisticsColumnNumberClassName } style={ {color: colorNumber2} }>
                    { reservedSeatQuotas }
                </div>
                <div className={ globalQuotaTypeChartStatisticsColumnName }>
                    Countries with reserved seats
                </div>
            </div>
        </div>
    );
};

const statisticsCaption = (
    numberReservedSeats: number,
    numberCandidateQuotas: number,
    numberBoth: number,
    theme: ThemeName,
) => {
    let colorForText: string = "";
    let colorForNumberReservedSeats: string = "";
    let colorForNumberCandidateQuotas: string = "";

    switch (theme) {
        case 'blue':
            colorForText = COLOR_05;
            colorForNumberReservedSeats = COLOR_25;
            colorForNumberCandidateQuotas = COLOR_26;
            break;

        case 'green':
            colorForText = COLOR_35;
            colorForNumberReservedSeats = COLOR_36;
            colorForNumberCandidateQuotas = COLOR_35;
            break;
    }

    return (
        <Grid>
            <Cell size={ 12 } sizeTablet={ 6 }>
                <div className={ quotaTypeChartStatisticsCaptionClassName }>
                    <div className={ quotaTypeChartStatisticsCaptionNumberClassName } style={ {color: colorForNumberReservedSeats} }>
                        { numberReservedSeats }
                    </div>
                    <div className={ quotaTypeChartStatisticsCaptionDescriptionClassName } style={ {color: colorForText} }>
                        Countries with reserved seats
                    </div>
                </div>
            </Cell>
            <Cell size={ 12 } sizeTablet={ 6 }>
                <div className={ quotaTypeChartStatisticsCaptionClassName }>
                    <div className={ quotaTypeChartStatisticsCaptionNumberClassName } style={ {color: colorForNumberCandidateQuotas} }>
                        { numberCandidateQuotas }
                    </div>
                    <div className={ quotaTypeChartStatisticsCaptionDescriptionClassName } style={ {color: colorForText} }>
                        Countries with candidate quotas
                    </div>
                </div>
                {  numberBoth > 0 && (
                    <div className={ quotaTypeChartStatisticsCaptionDescriptionSmallClassName } style={ {color: colorForText} }>
                        *{ numberBoth } countr{  numberBoth === 1 ? 'y' : 'ies' } { numberBoth === 1 ? 'has' : 'have' } both reserved seats and candidate quotas
                    </div>
                )}
            </Cell>
        </Grid>
    );
};

const QuotaTypeChart: React.FC<QuotaTypeChartProps> = ({
    profiles,
    profilesLocalLevel,
    countryCount = COUNTRY_COUNT,
    withCaption = false
}) => {
    const [{taxonomies}] = useAppState();
    const elementRef = createRef<HTMLDivElement>();
    const chartValues = profiles && getValuesForChart(profiles, taxonomies);
    const localLevelchartValues = profilesLocalLevel && getValuesForChart(profilesLocalLevel, taxonomies);

    const countryProfilesWithReservedSeats: CountryProfile[] = [];
    const countryProfilesWithCandidateQuota: CountryProfile[] = [];

    const profilesArray = (profiles || profilesLocalLevel || []);
    profilesArray.forEach(countryProfile => {
        countryProfile.typeOfQuotaIds.forEach(typeOfQuotaId => {
            switch (typeOfQuotaId) {
                case 14: return countryProfilesWithCandidateQuota.push(countryProfile);
                case 15: return countryProfilesWithReservedSeats.push(countryProfile);
            }
        });
    });

    const countryProfilesWithReservedSeatsCountryNames: string[] = countryProfilesWithReservedSeats
        .map(profile => getTaxonomyName(taxonomies, 'countries', profile.countryId));

    const countryProfilesWithCandidateQuotaCountryNames: string[] = countryProfilesWithCandidateQuota
        .map(profile => getTaxonomyName(taxonomies, 'countries', profile.countryId));

    countryProfilesWithReservedSeatsCountryNames.sort((a, b) => a > b ? 1 : -1);
    countryProfilesWithCandidateQuotaCountryNames.sort((a, b) => a > b ? 1 : -1);

    useEffect(() => {
        const fitFontSize = () => {
            setTimeout(() => {
                const element = elementRef.current;

                if (element) {
                    element.style.fontSize = `${element.offsetWidth / 2.8}px`;
                }
            }, 50);
        };

        fitFontSize();

        window.addEventListener('resize', fitFontSize);

        return () => {
            window.removeEventListener('resize', fitFontSize)
        };
    }, [elementRef]);

    const donutCaption = withCaption && <>
        <div className={ globalQuotaTypeChartLabelNumberClassName }>
            <DynamicNumber value={ profilesArray.length } integerOnly speed={ .4 } />
        </div>
        <div className={ globalQuotaTypeChartLabelTextClassName }>
            Total countries<br/>with Quotas*
        </div>
    </>;

    return (
        <div className={ globalQuotaTypeChartClassName }>
            <div className={ globalQuotaTypeChartDonutClassName }>
                <div
                    className={ classes(
                        globalQuotaTypeChartDonutContainerClassName,
                        (chartValues && localLevelchartValues) && globalQuotaTypeChartDoubleDonutContainerClassName
                    ) }
                    ref={ elementRef }>
                    <Donut2 valueSet={ [
                        localLevelchartValues && {
                            theme: 'green',
                            maxValue: countryCount,
                            values: [
                                {
                                    value: localLevelchartValues.withReservedSeats.count,
                                    tooltip: localLevelchartValues.withReservedSeats.tooltip.join(', ')
                                },
                                {
                                    value: localLevelchartValues.withCandidateQuota.count,
                                    tooltip: localLevelchartValues.withCandidateQuota.tooltip.join(', ')
                                },
                            ]
                        },
                        chartValues && {
                            theme: 'blue',
                            maxValue: countryCount,
                            values: [
                                {
                                    value: chartValues.withReservedSeats.count,
                                    tooltip: chartValues.withReservedSeats.tooltip.join(', ')
                                },
                                {
                                    value: chartValues.withCandidateQuota.count,
                                    tooltip: chartValues.withCandidateQuota.tooltip.join(', ')
                                }
                            ]
                        }
                    ] } children={ donutCaption } />
                </div>
            </div>
            { chartValues && localLevelchartValues && (
                <Grid className={ resetSpacingsClassName }>
                    <Cell size={ 12 } sizeTablet={ 6 } className={ resetSpacingsClassName }>
                        { statisticsColumn(
                            'Parliament',
                            chartValues.withCandidateQuota.count + chartValues.both.count,
                            chartValues.withReservedSeats.count + chartValues.both.count,
                            'blue',
                        ) }
                    </Cell>
                    <Cell size={ 12 } sizeTablet={ 6 } className={ resetSpacingsClassName }>
                        { statisticsColumn(
                            'Local government',
                            localLevelchartValues.withCandidateQuota.count + localLevelchartValues.both.count,
                            localLevelchartValues.withReservedSeats.count + localLevelchartValues.both.count,
                            'green',
                        ) }
                    </Cell>
                    <div className={ parliamentAndLocalGovernmentLinksClassName }>
                        <Spacer>
                            {"Explore Quota Analysis for "}
                            <Link to={ ROUTE_PAGE_QUOTA_ANALYSIS + '?profileType=parliament' } style={ {color: COLOR_25 } }>parliament</Link>
                            {" and "}
                            <Link to={ ROUTE_PAGE_QUOTA_ANALYSIS + '?profileType=localGovernment' } style={ {color: COLOR_35 } }>local government</Link>
                        </Spacer>
                    </div>
                </Grid>
            ) }
            { chartValues && !localLevelchartValues && (
                statisticsCaption(
                    chartValues.withReservedSeats.count + chartValues.both.count,
                    chartValues.withCandidateQuota.count + chartValues.both.count,
                    chartValues.both ? chartValues.both.count : 0,
                    'blue'
                )
            )}
            { localLevelchartValues && !chartValues && (
                statisticsCaption(
                    localLevelchartValues.withReservedSeats.count + localLevelchartValues.both.count,
                    localLevelchartValues.withCandidateQuota.count + localLevelchartValues.both.count,
                    localLevelchartValues.both ? localLevelchartValues.both.count : 0,
                    'green'
                )
            )}
        </div>
    )
};

export default QuotaTypeChart;
